import { readUnhandledEnumCase } from 'rpc-client-utils';

import Products, { ProductApiName } from 'merchandise-lib/constants/Product';

import {
  AVAILABLE_CURRENCY_CODES,
  type AvailableCurrencyCode,
} from '../constants/availableCurrencyCodes';
import type { AllProductLines } from './productLine';
import type {
  BaseDirectToBuyButtonProps,
  PublicCtaProps,
  PublicPurchaseMotion,
} from '../types/PublicPurchaseMotion';
import type {
  InAppPurchaseMotion,
  PurchaseMotion,
  TalkToSalesPurchaseMotion,
} from '../types/PurchaseMotion';

const ApiNamesSet = new Set<string>(Object.values(Products));

export const hasPublicPurchaseRequest = (
  ctaProps: PublicCtaProps | undefined
): ctaProps is BaseDirectToBuyButtonProps & {
  purchaseRequest: NonNullable<BaseDirectToBuyButtonProps['purchaseRequest']>;
  currencyCode?: never;
  terms?: never;
} => {
  return (
    ctaProps !== undefined &&
    'purchaseRequest' in ctaProps &&
    ctaProps.purchaseRequest !== undefined &&
    !('currencyCode' in ctaProps && ctaProps.currencyCode !== undefined)
  );
};

export const hasPublicCurrencyCode = (
  ctaProps: PublicCtaProps | undefined
): ctaProps is BaseDirectToBuyButtonProps & {
  purchaseRequest?: never;
  currencyCode: NonNullable<BaseDirectToBuyButtonProps['currencyCode']>;
} => {
  return (
    ctaProps !== undefined &&
    'currencyCode' in ctaProps &&
    ctaProps.currencyCode !== undefined &&
    !('purchaseRequest' in ctaProps && ctaProps.purchaseRequest !== undefined)
  );
};

export const isAvailableCurrencyCode = (
  currencyCode: unknown
): currencyCode is AvailableCurrencyCode =>
  typeof currencyCode === 'string' &&
  Object.keys(AVAILABLE_CURRENCY_CODES).includes(currencyCode);

export const isApiName = (apiName: string): apiName is ProductApiName => {
  return (
    apiName !== null &&
    apiName !== undefined &&
    typeof apiName === 'string' &&
    ApiNamesSet.has(apiName)
  );
};

export const isValidTalkToSalesPurchaseMotion = (
  purchaseMotion: PurchaseMotion
): purchaseMotion is TalkToSalesPurchaseMotion =>
  ['BLOCKED', 'TALK_TO_SALES'].includes(readUnhandledEnumCase(purchaseMotion));

export const isValidProductLine = (
  productLine: any
): productLine is AllProductLines =>
  ['SALES', 'MARKETING', 'SERVICE', 'CMS', 'COMMERCE', 'OPERATIONS'].includes(
    productLine
  );

export const isPublicPurchaseMotion = (
  purchaseMotion: PurchaseMotion
): purchaseMotion is PublicPurchaseMotion =>
  ['BUY_TOUCHLESS', 'TRIAL', 'TALK_TO_SALES'].includes(
    readUnhandledEnumCase(purchaseMotion)
  );

export const IN_APP_PURCHASE_MOTIONS: readonly InAppPurchaseMotion[] = [
  'ASSIGN_SEAT',
  'BUY_ADDITIONAL_CONTACTS',
  'BUY_ADDITIONAL_SEATS',
  'BUY_TOUCHLESS',
  'PRICING_PAGE_REDIRECT',
  'REQUEST_PRODUCT',
  'REQUEST_SEAT',
  'TALK_TO_SALES',
  'TRIAL',
  'VIEW_ACCOUNT',
];

export const isInAppPurchaseMotion = (
  purchaseMotion: PurchaseMotion
): purchaseMotion is InAppPurchaseMotion =>
  (IN_APP_PURCHASE_MOTIONS as readonly string[]).includes(
    readUnhandledEnumCase(purchaseMotion)
  );
